<template>
  <modal
    :title="isEditView ? 'Edit Project' : 'Create a Project'"
    :show="show"
    :hide-footer="true"
    @hidden="hideModal"
  >
    <div class="text-secondary text-normal">
      A project is a time-limited initiative that involves planning and execution to achieve a
      defined objective.
    </div>
    <form @submit.prevent="onSubmit">
      <div class="my-4">
        <div class="d-flex flex-column">
          <label class="text-normal">Name your Project</label>
          <div class="d-flex align-center">
            <input
              class="input-field text-normal team-name mx-n2 flex-grow-1"
              :class="{ 'errored-field': errors.title }"
              type="text"
              v-model="title"
              v-autofocus
              v-no-capture
            />
            <color-picker class="ml-3" v-model="color" :palette="palette" />
          </div>
        </div>
        <template v-if="errors.title">
          <div v-for="(error, index) in errors.title" :key="index" class="field-error-message">
            {{ error }}
          </div>
        </template>
      </div>
      <div v-if="typeof errors === 'string'" class="field-error-message">
        {{ errors }}
      </div>
      <submit-buttons
        class="mt-5 mb-3"
        :is-loading="isSubmitting"
        :submit-text="isEditView ? 'Save' : 'Create project'"
        cancel-text="I changed by mind"
        @save="onSubmit"
        @cancel="hideModal"
      >
      </submit-buttons>
    </form>
  </modal>
</template>

<script>
import ColorPicker from '@/components/initiative/ColorPicker.vue'
import Modal from '@/components/Modal'
import SubmitButtons from '@/components/SubmitButtons'
import { PROJECT_PALETTE } from '@/constants/project'
import EventBus from '@/EventBus'
import EventBusMixin from '@/mixins/EventBusMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import TeamMixin from '@/mixins/TeamMixin'
import { getProjectPageRouteConfig } from '@/utils/navigationConfigs'
import * as R from 'ramda'
import { mapActions, mapGetters } from 'vuex'

/**
 * This component is used to create/edit/delete/archive a team
 */
export default {
  name: 'ManageProject',
  props: ['show', 'teamId', 'projectId', 'orgId'],
  model: {
    prop: 'show'
  },
  components: {
    Modal,
    SubmitButtons,
    ColorPicker
  },
  mixins: [TeamMixin, EventBusMixin, FeaturesMixin],
  data() {
    const project = this.projectId && this.$store.getters['project/projectById'](this.projectId)
    return {
      color: project?.color || PROJECT_PALETTE[0],
      title: project?.title,
      errors: {},
      isSubmitting: false,
      palette: PROJECT_PALETTE
    }
  },
  computed: {
    ...mapGetters('project', ['projectById']),
    isEditView() {
      // true indicates that component is editing a project, else it is creating a project
      return Boolean(this.projectId)
    },
    team() {
      return this.$store.getters.teamById(this.teamId)
    }
  },
  methods: {
    ...mapActions('project', ['createProject', 'updateProject']),
    goToProjectPage(project) {
      const location = getProjectPageRouteConfig({
        orgId: project.organizationId,
        projectId: project.id
      })
      this.$router.push(location)
    },
    async onSubmit() {
      try {
        this.isSubmitting = true
        if (this.isEditView) {
          await this.updateProject({
            orgId: this.orgId,
            projectId: this.projectId,
            payload: {
              title: this.title,
              color: this.color
            }
          })
          EventBus.$emit('ADD_TOAST', { message: 'Changes have been saved successfully' })
          this.hideModal()
        } else {
          const project = await this.createProject({
            orgId: this.orgId,
            payload: {
              title: this.title,
              color: this.color,
              teamId: this.teamId
            }
          })
          EventBus.$emit('ADD_TOAST', { message: 'Project has been created successfully' })
          this.goToProjectPage(project)
          this.hideModal()
          /**
           * @emits
           * This is emitted when a new team is created
           */
          this.$emit('add', project)
        }
      } catch (e) {
        this.errors =
          R.path(['response', 'data', 'detail'], e) || R.path(['response', 'data', 'message'], e)
      }
      this.isSubmitting = false
    },
    hideModal() {
      this.$emit('input', false)
    }
  },
  async mounted() {
    this.listenToEventBus('SUBMIT_FORM', () => {
      this.show && this.onSubmit()
    })
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/forms';
@import '~@/styles/errors';

.access-modifier {
  margin-top: 2px;
}
</style>
